import { FiEdit2 } from 'react-icons/fi';
import Badge from '../../../../components/ui/Badge';
import PassFailToggle from '../PassFailToggle';

/**
 * Default mobile test card for most assets (non-RCD / specialised layouts).
 *
 * Public interface: asset identity props + controlled `result` / `onResultChange`
 * (+ optional `onEdit`, `tested`, `isSynced`). Hides status chrome (inner bar,
 * Card-style left border, edit tint), the Pass/Fail toggle wiring, and the
 * saved/read-only + sync-badge state once a result has been recorded.
 *
 * Props:
 *   equipmentCode — asset / equipment code shown large (e.g. "1")
 *   make, model, capacity — joined under the code as "Make / Model / Capacity"
 *   location — top-right location text (e.g. "B00K - R0007")
 *   result — `'pass' | 'fail' | null`
 *   onResultChange — (next) => void
 *   tested — true once a result is saved for this asset; locks the toggle
 *   isSynced — when `tested`, whether the save is confirmed ("Saving…" if false)
 *   onEdit — optional edit handler
 *   disabled — disables controls regardless of `tested`
 *   className — extra class(es) on the card
 */
export default function DefaultTestCard({
  equipmentCode,
  make,
  model,
  capacity,
  location,
  result = null,
  onResultChange,
  tested = false,
  isSynced = true,
  onEdit,
  disabled = false,
  className = '',
  ...props
}) {
  const state = result === 'pass' || result === 'fail' ? result : 'unset';
  const details = [make, model, capacity].filter(Boolean).join(' / ');
  const cardStatus = result === 'pass' ? 'success' : result === 'fail' ? 'danger' : undefined;
  const locked = disabled || tested;

  return (
    <article
      className={[
        'mobile-test-card',
        cardStatus ? `card--${cardStatus}` : '',
        className,
      ].filter(Boolean).join(' ')}
      data-state={state}
      {...props}
    >
      <div className="mobile-test-card__header">
        <div className="mobile-test-card__lead">
          <span className="mobile-test-card__sideline" aria-hidden="true" />
          <div className="mobile-test-card__identity">
            <div className="mobile-test-card__code">{equipmentCode}</div>
            {details ? (
              <div className="mobile-test-card__details">{details}</div>
            ) : null}
          </div>
        </div>

        <div className="mobile-test-card__meta">
          {location ? (
            <span className="mobile-test-card__location">{location}</span>
          ) : null}
          {tested ? (
            <Badge variant={!isSynced ? 'warning' : result === 'fail' ? 'danger' : 'success'}>
              {!isSynced ? 'Saving…' : result === 'fail' ? 'FAILED' : 'PASSED'}
            </Badge>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              className="mobile-test-card__edit"
              onClick={onEdit}
              disabled={disabled}
              aria-label="Edit asset"
              title="Edit"
            >
              <FiEdit2 size={16} aria-hidden />
            </button>
          ) : null}
        </div>
      </div>

      <PassFailToggle
        value={result}
        onChange={onResultChange}
        disabled={locked}
        ariaLabel={`Result for ${equipmentCode}`}
      />
    </article>
  );
}
